import { useCallback, useEffect, useMemo, useState } from 'react';
import * as service from '../../services/asplanService';
import type { SchreibKontext } from '../../services/asplanService';
import { fortschritt, pruefeAutomatisch, warnungen } from './asplanLogic';
import { planeUebernahme, type UebernahmePlan } from './bestandUebernahme';
import { entwuerfeAusVorschlaegen, type KatalogVorschlag } from './ablage/katalogVorschlaege';
import type { ReportMetadata } from '../../types';
// Kalendertag der ORTSZEIT. Ein auf zehn Zeichen gekuerzter ISO-Zeitstempel
// liefert das UTC-Datum: eine Aufstufung um 00:30 MESZ waere auf den Vortag datiert, und
// `geltendeKlasse` sortiert ueber genau dieses Feld — eine am Vorabend
// erfasste Abstufung rutschte dahinter und wuerde gelten.
import { heuteIso } from '../sige-einweisung/einweisungLogic';
import { clusterEinstufung, feldEinstufung } from './einstufung';
import { schutzklasseVorschlag } from './schutzklassen';
import type { AsplanBundle, Cluster, Rasterfeld } from './types';

/**
 * Der Hook des Moduls „A+S-Plan": Laden, Speichern, Ableiten.
 *
 * ═══ WARUM DIE ABLEITUNGEN HIER LIEGEN UND NICHT IN DER KOMPONENTE ════════
 * Einstufung, Vorschlag und Warnungen haengen an fast allem: eine neue Probe
 * aendert die Klasse eines Feldes, die Klasse eines Bereichs, den Vorschlag
 * fuer die Schutzklasse und mehrere Warnungen zugleich. Wuerde jede
 * Komponente das fuer sich rechnen, rechneten sie unterschiedlich oft und
 * — schlimmer — zu unterschiedlichen Zeitpunkten.
 *
 * Deshalb: EINE Quelle. Der Hook haelt das Buendel, rechnet daraus ab, und
 * die Komponenten lesen nur. Die Rechnung selbst steht in reinen Funktionen
 * daneben und ist ohne React geprueft.
 *
 * ═══ NACH JEDEM SCHREIBEN NEU LADEN ═══════════════════════════════════════
 * Der Hook schreibt nicht in den lokalen Zustand hinein, sondern laedt das
 * Buendel neu. Das ist eine Umdrehung mehr, aber es gibt keinen zweiten Ort,
 * an dem der Stand auseinanderlaufen kann — und genau das ist in diesem Repo
 * schon mehrfach die Ursache stiller Fehler gewesen.
 */

export interface AsplanLage {
    bundle: AsplanBundle | null;
    laedt: boolean;
    fehler: string | null;
}

export const useAsplan = (projectId: string, nurAnsicht: boolean) => {
    const [lage, setLage] = useState<AsplanLage>({ bundle: null, laedt: true, fehler: null });
    const [arbeitet, setArbeitet] = useState(false);

    /**
     * `still` laedt nach, ohne den Ladezustand zu setzen: das Modul ersetzt
     * sich bei `laedt` komplett durch „Lädt …", und eine Ansicht, die nach
     * jedem Schreiben kurz verschwindet, verliert Fokus und Scrollstand.
     */
    const laden = useCallback(async (still = false) => {
        setLage(l => ({ ...l, laedt: still ? l.laedt : true, fehler: null }));
        try {
            const bundle = await service.fetchAsplanBundle(projectId);
            setLage({ bundle, laedt: false, fehler: null });
        } catch (e) {
            setLage({ bundle: null, laedt: false, fehler: (e as Error).message });
        }
    }, [projectId]);

    useEffect(() => { void laden(); }, [laden]);

    /** Der Schreibkontext, wie ihn jeder schreibende Aufruf verlangt. */
    const ctx = useMemo<SchreibKontext>(() => ({
        nurAnsicht,
        status: lage.bundle?.plan.status,
    }), [nurAnsicht, lage.bundle?.plan.status]);

    /**
     * Fuehrt einen schreibenden Aufruf aus und laedt danach neu.
     *
     * Der Fehler wird NICHT verschluckt: er landet in `fehler` und wird
     * angezeigt. Ein stillschweigend fehlgeschlagener Schreibvorgang ist die
     * schlechteste aller Rueckmeldungen — der Anwender tippt weiter.
     */
    const schreiben = useCallback(async <T>(vorgang: () => Promise<T>): Promise<T | null> => {
        setArbeitet(true);
        try {
            const ergebnis = await vorgang();
            await laden(true);
            return ergebnis;
        } catch (e) {
            setLage(l => ({ ...l, fehler: (e as Error).message }));
            return null;
        } finally {
            setArbeitet(false);
        }
    }, [laden]);

    // ═══════════════════════════════════════════════════════════════════════
    // ABLEITUNGEN
    // ═══════════════════════════════════════════════════════════════════════

    const b = lage.bundle;

    /** Einstufung je Rasterfeld, nach `feld_nr` ansprechbar. */
    const feldLage = useMemo(() => {
        const map = new Map<string, ReturnType<typeof feldEinstufung>>();
        if (!b) return map;
        for (const f of b.rasterfelder) {
            map.set(f.feld_nr, feldEinstufung(b.proben.filter(p => p.rasterfeld_id === f.id)));
        }
        return map;
    }, [b]);

    /** Einstufung und Schutzklassenvorschlag je Arbeitsbereich. */
    const bereichsLage = useMemo(() => {
        const map = new Map<string, {
            einstufung: ReturnType<typeof clusterEinstufung>;
            vorschlag: ReturnType<typeof schutzklasseVorschlag>;
        }>();
        if (!b) return map;

        for (const c of b.cluster) {
            const felder = c.felder.map(nr => {
                const feld = b.rasterfelder.find(f => f.feld_nr === nr);
                return {
                    feld_nr: nr,
                    proben: feld ? b.proben.filter(p => p.rasterfeld_id === feld.id) : [],
                };
            });
            const einstufung = clusterEinstufung(felder);
            map.set(c.kennung, {
                einstufung,
                vorschlag: schutzklasseVorschlag(c, einstufung, b.schutzklassen),
            });
        }
        return map;
    }, [b]);

    const befunde = useMemo(() => (b ? warnungen(b) : []), [b]);
    const checks = useMemo(() => (b ? pruefeAutomatisch(b) : []), [b]);
    const stand = useMemo(() => (b ? fortschritt(b) : null), [b]);

    /** Warnungen eines Kapitels — fuer die Ampel in der Kapitelspalte. */
    const befundeZuKapitel = useCallback(
        (kapitel: string) => befunde.filter(w => w.kapitel === kapitel),
        [befunde],
    );

    // ═══════════════════════════════════════════════════════════════════════
    // VORGAENGE
    // ═══════════════════════════════════════════════════════════════════════

    const anlegen = useCallback((titel: string) =>
        schreiben(() => service.ensureAsplan(ctx, projectId, titel)), [schreiben, ctx, projectId]);

    const planAendern = useCallback((updates: Parameters<typeof service.updateAsplan>[2]) => {
        if (!b) return Promise.resolve(null);
        return schreiben(() => service.updateAsplan(ctx, b.plan.id, updates));
    }, [schreiben, ctx, b]);

    const freigeben = useCallback((von: string, revLabel: string) => {
        if (!b) return Promise.resolve(null);
        return schreiben(() => service.freigebenAsplan(ctx, b.plan, von, revLabel));
    }, [schreiben, ctx, b]);

    const revisionEroeffnen = useCallback((von: string, grund: string) => {
        if (!b) return Promise.resolve(null);
        return schreiben(() => service.neueRevision(ctx, b.plan, von, grund));
    }, [schreiben, ctx, b]);

    /**
     * Legt ein Rasterfeld an.
     *
     * `project_id` und `asplan_id` kommen aus dem Buendel und nicht vom
     * Aufrufer: sie sind Bestandteil jeder Zeile (die Policy liest
     * `project_id` direkt), und ein Aufrufer, der sie mitgeben muesste,
     * vergaesse sie irgendwann.
     */
    const feldAnlegen = useCallback((feld: Partial<Rasterfeld> & { feld_nr: string }) => {
        if (!b) return Promise.resolve(null);
        return schreiben(() => service.createRasterfeld(ctx, {
            project_id: b.plan.project_id,
            asplan_id: b.plan.id,
            feld_nr: feld.feld_nr,
            bezeichnung: feld.bezeichnung ?? '',
            flaeche_qm: feld.flaeche_qm ?? null,
            lage_hinweis: feld.lage_hinweis ?? '',
            raster_spalte: feld.raster_spalte ?? null,
            raster_zeile: feld.raster_zeile ?? null,
            sortierung: b.rasterfelder.length,
        }));
    }, [schreiben, ctx, b]);

    const bereichAnlegen = useCallback((c: Partial<Cluster> & { kennung: string }) => {
        if (!b) return Promise.resolve(null);
        return schreiben(() => service.createCluster(ctx, {
            project_id: b.plan.project_id,
            asplan_id: b.plan.id,
            kennung: c.kennung,
            bezeichnung: c.bezeichnung ?? '',
            baumassnahme: c.baumassnahme ?? '',
            felder: c.felder ?? [],
            leitparameter: c.leitparameter ?? '',
            schutzklasse_key: c.schutzklasse_key ?? '',
            schutzklasse_vorschlag: '',
            schutzklasse_begruendung: '',
            befundlage: '',
            aushubstrategie: '',
            besonderheit: '',
            art: c.art ?? 'regel',
            umgebung: c.umgebung ?? {},
            sige_arbeitsschritt_ids: [],
            sortierung: b.cluster.length,
        }));
    }, [schreiben, ctx, b]);

    // ═══════════════════════════════════════════════════════════════════════
    // VORGAENGE DER LISTEN
    // ═══════════════════════════════════════════════════════════════════════
    //
    // `project_id` und `asplan_id` kommen aus dem Buendel und nicht vom
    // Aufrufer: sie stehen an JEDER Zeile (die Policy liest `project_id`
    // direkt), und ein Aufrufer, der sie mitgeben muesste, vergaesse sie
    // irgendwann — dann scheitert der Insert an der RLS, und die Meldung
    // fuehrt in die Irre.

    /** Die Zugehoerigkeit, die jede neue Zeile braucht. */
    const zugehoerig = useCallback(() => ({
        project_id: b?.plan.project_id ?? '',
        asplan_id: b?.plan.id ?? '',
    }), [b]);

    const probeAnlegen = useCallback((rasterfeld_id: string, mischprobe: string) => {
        if (!b) return Promise.resolve(null);
        return schreiben(() => service.createProbe(ctx, {
            ...zugehoerig(),
            rasterfeld_id,
            mischprobe,
            zusammensetzung: '', tiefe_von: null, tiefe_bis: null,
            untersuchungsumfang: '', befunde: [], einstufung: null,
            massgeblich_stoff: '', vorbehalt: false, vorbehalt_grund: '',
            wiedervorlage_am: null, wiedervorlage_wer: '', bemerkung: '',
            sortierung: b.proben.filter(p => p.rasterfeld_id === rasterfeld_id).length,
        }));
    }, [schreiben, ctx, b, zugehoerig]);

    const taetigkeitAnlegen = useCallback((cluster_kennung: string, bezeichnung: string) => {
        if (!b) return Promise.resolve(null);
        return schreiben(() => service.createTaetigkeit(ctx, {
            ...zugehoerig(),
            cluster_kennung,
            taetigkeit: bezeichnung,
            arbeitsverfahren: '', rolle_key: '', personenkreis: '',
            dauer_min: null, haeufigkeit: '', stoffe: [], expositionswege: [],
            // NULL und nicht 'keine': „noch nicht beurteilt" ist etwas anderes
            // als „keine Exposition".
            exposition: null, exposition_begruendung: '', emission: {},
            schutzklasse_key: '', massnahmen: [],
            sortierung: b.taetigkeiten.length,
        }));
    }, [schreiben, ctx, b, zugehoerig]);

    const schutzklasseAnlegen = useCallback((key: string) => {
        if (!b) return Promise.resolve(null);
        const hoechster = b.schutzklassen.reduce((m, k) => Math.max(m, k.rang), 0);
        return schreiben(() => service.createSchutzklasse(ctx, {
            ...zugehoerig(),
            key,
            rang: hoechster + 1,
            bezeichnung: '', anwendungsbereich: '', psa: [], zusatz: [],
            atemschutz: {}, schutzkleidung: {}, handschutz: {}, fussschutz: {},
            tragezeit: {}, handhabung: [], farbe: '#e2e8f0',
            sortierung: b.schutzklassen.length,
        }));
    }, [schreiben, ctx, b, zugehoerig]);

    const beteiligtenAnlegen = useCallback((funktion: string) => {
        if (!b) return Promise.resolve(null);
        return schreiben(() => service.createBeteiligter(ctx, {
            ...zugehoerig(),
            funktion_key: '', funktion,
            organisation: '', person: '', anschrift: '',
            telefon: '', mobil: '', email: '', gilt_fuer: [],
            quelle: 'manuell',
            sortierung: b.beteiligte.length,
        }));
    }, [schreiben, ctx, b, zugehoerig]);

    /**
     * Uebernimmt die Beteiligten des Projekts — Vertreterliste, Bauherr,
     * Auftraggeber.
     *
     * NUR auf ausdruecklichen Knopfdruck — nie beim Laden. Eine Vorbelegung,
     * die sich selbst speichert, mutiert Bestandsdatensaetze rueckwirkend
     * (Lehre aus Handbuch und Planungsphase).
     *
     * Was passiert, entscheidet `planeUebernahme` und sonst nichts: neue
     * Zeilen mit `quelle = 'projekt'`, uebernommene Zeilen nachgezogen,
     * manuelle Zeilen unangetastet, Entfallenes ausgewiesen statt geloescht.
     * Alles in EINEM `schreiben`, damit das Buendel einmal und nicht
     * siebenmal neu geladen wird.
     */
    const beteiligteUebernehmen = useCallback((
        metadata: Partial<ReportMetadata>,
    ): Promise<UebernahmePlan | null> => {
        if (!b) return Promise.resolve(null);
        const vorhaben = planeUebernahme(metadata, b.beteiligte);
        if (vorhaben.neu.length === 0 && vorhaben.aktualisieren.length === 0) {
            return Promise.resolve(vorhaben);
        }
        return schreiben(async () => {
            let lauf = b.beteiligte.length;
            for (const v of vorhaben.neu) {
                await service.createBeteiligter(ctx, {
                    ...zugehoerig(),
                    funktion_key: v.funktion_key, funktion: v.funktion,
                    organisation: v.organisation, person: v.person,
                    anschrift: v.anschrift, telefon: v.telefon,
                    mobil: v.mobil, email: v.email, gilt_fuer: [],
                    quelle: 'projekt',
                    sortierung: lauf++,
                });
            }
            for (const a of vorhaben.aktualisieren) {
                await service.updateBeteiligter(ctx, a.id, a.aenderung);
            }
            return vorhaben;
        });
    }, [schreiben, ctx, b, zugehoerig]);

    const unternehmenAnlegen = useCallback((unternehmen: string) => {
        if (!b) return Promise.resolve(null);
        return schreiben(() => service.createUnternehmen(ctx, {
            ...zugehoerig(),
            unternehmen, gewerk: '', anschrift: '', rolle_im_projekt: 'nu',
            aufsichtsfuehrender: '', aufsicht_telefon: '', anzahl_ma: null,
            sachkunde_vorgelegt: false, sachkunde_datum: null,
            arbeitsmediziner: '', arbeitsmediziner_anschrift: '',
            arbeitsmediziner_status: 'offen',
            erklaerung_status: 'offen', erklaerung_datum: null,
            dokument_id: null, anzeige_bg_am: null, bemerkung: '',
            sortierung: b.unternehmen.length,
        }));
    }, [schreiben, ctx, b, zugehoerig]);

    const unterlageAnlegen = useCallback((bezeichnung: string) => {
        if (!b) return Promise.resolve(null);
        return schreiben(() => service.createUnterlage(ctx, {
            ...zugehoerig(),
            art: 'grundlage', nr: '', herausgeber: '', bezeichnung,
            datum: null, kennung: '', dokument_id: null, nachgereicht: false,
            sortierung: b.unterlagen.length,
        }));
    }, [schreiben, ctx, b, zugehoerig]);

    /**
     * Uebernimmt Vorschlaege aus dem Katalog mitgeltender Dokumente in
     * Kapitel 9 — einen oder alle.
     *
     * Die Zeilen entstehen in `entwuerfeAusVorschlaegen` gegen den Bestand
     * dieses Augenblicks: was zwischen Anzeige und Klick schon angelegt wurde,
     * faellt weg. Alles in EINEM `schreiben`, damit das Buendel einmal und
     * nicht je Zeile neu geladen wird.
     *
     * Bricht die Reihe mittendrin ab, wird trotzdem neu geladen: `schreiben`
     * laedt im Fehlerfall nicht, und eine Liste ohne die schon angelegten
     * Zeilen boete sie beim naechsten Klick ein zweites Mal an.
     */
    const unterlagenAusKatalog = useCallback((vorschlaege: readonly KatalogVorschlag[]) => {
        if (!b) return Promise.resolve(null);
        const entwuerfe = entwuerfeAusVorschlaegen(vorschlaege, b.unterlagen);
        if (entwuerfe.length === 0) return Promise.resolve(0);
        return schreiben(async () => {
            let angelegt = 0;
            try {
                for (const e of entwuerfe) {
                    await service.createUnterlage(ctx, { ...zugehoerig(), ...e });
                    angelegt++;
                }
            } catch (e) {
                if (angelegt === 0) throw e;
                await laden(true);
                throw new Error(`${angelegt} von ${entwuerfe.length} Unterlagen übernommen, `
                    + `dann abgebrochen: ${(e as Error).message}`);
            }
            return angelegt;
        });
    }, [schreiben, laden, ctx, b, zugehoerig]);

    const rolleAnlegen = useCallback((rolle: string) => {
        if (!b) return Promise.resolve(null);
        return schreiben(() => service.createRolle(ctx, {
            ...zugehoerig(),
            rolle_key: '', rolle,
            cluster: [], schritte: [], situationen: [],
            sortierung: b.rollen.length,
        }));
    }, [schreiben, ctx, b, zugehoerig]);

    /**
     * Erfasst eine Auf- oder Abstufung.
     *
     * `bautagebuch` beginnt bei `false` und nicht bei `true`: der Eintrag ins
     * Bautagebuch ist eine Handlung auf der Baustelle, kein Haken im
     * Programm. Wer ihn vorbelegt, bekommt ihn geschenkt.
     */
    const stufungAnlegen = useCallback((
        cluster_kennung: string,
        richtung: 'auf' | 'ab',
        von_key: string,
        nach_key: string,
    ) => {
        if (!b) return Promise.resolve(null);
        return schreiben(() => service.createStufung(ctx, {
            ...zugehoerig(),
            cluster_kennung, bereich_hinweis: '',
            richtung, von_key, nach_key,
            anlass: '', grundlage: '',
            entschieden_von: '', entschieden_am: heuteIso(),
            bestaetigt_von: '', bestaetigt_am: null,
            bautagebuch: false,
        }));
    }, [schreiben, ctx, b, zugehoerig]);

    /**
     * Legt eine Skizze an oder aendert die vorhandene desselben Typs.
     *
     * Je Typ hoechstens eine Skizze: der Plan hat EINE Schwarz-Weiss-Anlage
     * und EINEN Zonenplan. Mehrere Fassungen desselben Bildes im selben Plan
     * waeren eine Einladung, die falsche zu drucken.
     */
    const skizzeSpeichern = useCallback((
        typ: import('./types').SkizzenTyp,
        titel: string,
        parameter: Record<string, unknown>,
    ) => {
        if (!b) return Promise.resolve(null);
        const vorhanden = b.skizzen.find(s => s.typ === typ);
        if (vorhanden) {
            return schreiben(() => service.updateSkizze(ctx, vorhanden.id, { parameter, titel }));
        }
        return schreiben(() => service.createSkizze(ctx, {
            ...zugehoerig(),
            typ, titel, kapitel_key: '', beschreibung: '',
            parameter, hintergrund_dokument_id: null,
            sortierung: b.skizzen.length,
        }));
    }, [schreiben, ctx, b, zugehoerig]);

    /**
     * Waehlt einen Textbaustein ab oder schreibt seinen Wortlaut um.
     *
     * `upsert` von Hand: der Eintrag entsteht erst, wenn jemand etwas
     * entscheidet. Ein Bestand aus lauter „aktiv: true"-Zeilen saehe aus wie
     * eine getroffene Auswahl und waere doch nur die Vorbelegung.
     */
    const bausteinSetzen = useCallback((
        kapitel_key: string,
        baustein_key: string,
        u: { aktiv?: boolean; text_override?: string },
    ) => {
        if (!b) return Promise.resolve(null);
        const vorhanden = b.bausteine.find(
            x => x.kapitel_key === kapitel_key && x.baustein_key === baustein_key);
        if (vorhanden) {
            return schreiben(() => service.updateBaustein(ctx, vorhanden.id, u));
        }
        return schreiben(() => service.createBaustein(ctx, {
            ...zugehoerig(),
            kapitel_key, baustein_key,
            aktiv: u.aktiv ?? true,
            text_override: u.text_override ?? '',
            sortierung: b.bausteine.length,
        }));
    }, [schreiben, ctx, b, zugehoerig]);

    const pruefstandSetzen = useCallback((
        check_key: string,
        status: import('./types').PruefStatus,
        bemerkung: string,
    ) => {
        if (!b) return Promise.resolve(null);
        return schreiben(() => service.setzePruefstand(ctx, {
            ...zugehoerig(),
            check_key, status, bemerkung,
            geprueft_von: '', geprueft_am: heuteIso(),
        }));
    }, [schreiben, ctx, b, zugehoerig]);

    /** Ein Aendern-Vorgang je Tabelle — dieselbe Form fuer alle Listen. */
    const aendern = useMemo(() => ({
        feld: (id: string, u: Parameters<typeof service.updateRasterfeld>[2]) =>
            void schreiben(() => service.updateRasterfeld(ctx, id, u)),
        probe: (id: string, u: Parameters<typeof service.updateProbe>[2]) =>
            void schreiben(() => service.updateProbe(ctx, id, u)),
        bereich: (id: string, u: Parameters<typeof service.updateCluster>[2]) =>
            void schreiben(() => service.updateCluster(ctx, id, u)),
        taetigkeit: (id: string, u: Parameters<typeof service.updateTaetigkeit>[2]) =>
            void schreiben(() => service.updateTaetigkeit(ctx, id, u)),
        schutzklasse: (id: string, u: Parameters<typeof service.updateSchutzklasse>[2]) =>
            void schreiben(() => service.updateSchutzklasse(ctx, id, u)),
        beteiligter: (id: string, u: Parameters<typeof service.updateBeteiligter>[2]) =>
            void schreiben(() => service.updateBeteiligter(ctx, id, u)),
        unternehmen: (id: string, u: Parameters<typeof service.updateUnternehmen>[2]) =>
            void schreiben(() => service.updateUnternehmen(ctx, id, u)),
        unterlage: (id: string, u: Parameters<typeof service.updateUnterlage>[2]) =>
            void schreiben(() => service.updateUnterlage(ctx, id, u)),
        rolle: (id: string, u: Parameters<typeof service.updateRolle>[2]) =>
            void schreiben(() => service.updateRolle(ctx, id, u)),
        stufung: (id: string, u: Parameters<typeof service.updateStufung>[2]) =>
            void schreiben(() => service.updateStufung(ctx, id, u)),
    }), [schreiben, ctx]);

    /** Ein Loesch-Vorgang je Tabelle. */
    const loeschen = useMemo(() => ({
        feld: (id: string) => void schreiben(() => service.deleteRasterfeld(ctx, id)),
        probe: (id: string) => void schreiben(() => service.deleteProbe(ctx, id)),
        bereich: (id: string) => void schreiben(() => service.deleteCluster(ctx, id)),
        taetigkeit: (id: string) => void schreiben(() => service.deleteTaetigkeit(ctx, id)),
        schutzklasse: (id: string) => void schreiben(() => service.deleteSchutzklasse(ctx, id)),
        beteiligter: (id: string) => void schreiben(() => service.deleteBeteiligter(ctx, id)),
        unternehmen: (id: string) => void schreiben(() => service.deleteUnternehmen(ctx, id)),
        unterlage: (id: string) => void schreiben(() => service.deleteUnterlage(ctx, id)),
        rolle: (id: string) => void schreiben(() => service.deleteRolle(ctx, id)),
        // Eine Stufung wird NICHT geloescht: sie ist ein Vorgang, kein
        // Stammdatensatz. Deshalb bietet der Service dafuer auch nichts an.
    }), [schreiben, ctx]);

    return {
        ...lage,
        arbeitet,
        ctx,
        laden,
        schreiben,
        // Ableitungen
        feldLage,
        bereichsLage,
        befunde,
        befundeZuKapitel,
        checks,
        stand,
        // Vorgaenge
        anlegen,
        planAendern,
        freigeben,
        revisionEroeffnen,
        feldAnlegen,
        bereichAnlegen,
        probeAnlegen,
        taetigkeitAnlegen,
        schutzklasseAnlegen,
        beteiligtenAnlegen,
        beteiligteUebernehmen,
        unternehmenAnlegen,
        unterlageAnlegen,
        unterlagenAusKatalog,
        pruefstandSetzen,
        rolleAnlegen,
        stufungAnlegen,
        skizzeSpeichern,
        bausteinSetzen,
        aendern,
        loeschen,
        service,
    };
};
